import React, { useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import { useAppSelector } from "../../app/hooks";
import { selectRepos } from "../library/librarySlice";
import { selectUser } from "../auth/authSlice";
import { apiCreateAgent } from "../../services/AgentAPI";

export interface CreateAgentDialogProps {
  open: boolean;
  onClose: () => void;
  /** Called after a successful create so the list can refresh. */
  onCreated: () => void;
}

export default function CreateAgentDialog({ open, onClose, onCreated }: CreateAgentDialogProps) {
  const repos = useAppSelector(selectRepos);
  const user = useAppSelector(selectUser);

  const [name, setName] = useState("");
  const [repoId, setRepoId] = useState<number | "">("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const orgId = user?.default_organization_id;
  const wsId = user?.default_workspace_id;

  const reset = () => {
    setName("");
    setRepoId("");
    setError(null);
  };

  const canCreate =
    name.trim() !== "" && repoId !== "" && orgId != null && wsId != null && !busy;

  const handleCreate = () => {
    if (!canCreate || orgId == null || wsId == null) return;
    setBusy(true);
    setError(null);
    apiCreateAgent(orgId, wsId, { name: name.trim(), repo_id: repoId as number })
      .then(() => {
        reset();
        setBusy(false);
        onClose();
        onCreated();
      })
      .catch((e) => {
        setBusy(false);
        setError(typeof e === "string" ? e : "Could not create agent");
      });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>New agent</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
        <TextField
          autoFocus
          label="Agent name"
          size="small"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
        />

        <FormControl size="small" fullWidth>
          <InputLabel id="agent-repo-label">Repo</InputLabel>
          <Select
            labelId="agent-repo-label"
            label="Repo"
            value={repoId}
            onChange={(e) => setRepoId(e.target.value === "" ? "" : Number(e.target.value))}
          >
            {repos.length === 0 && (
              <MenuItem value="" disabled>
                No repos loaded
              </MenuItem>
            )}
            {repos.map((r) => (
              <MenuItem key={r.id} value={r.id}>
                {r.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {error && (
          <Typography variant="caption" color="error">
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleCreate} disabled={!canCreate}>
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}
